import MessageHelper from '../../helpers/message-helper';
import StockForecast from '../../model/forecast-stock-data';
import Tooltip, { TippyHTMLElement } from '../shared/tooltip';

export default class DeliveryAvailability {
  static readonly ASYNC_KEY = 'deliveryAvailability';

  private readonly ICON_ARROW = `<img class="icon -small" 
    src="${window.ACC.config.commonResourcePath}/images/icons/circle-w-arrow.png" alt=""/>`;

  private readonly ICON_CHECK = `<img class="icon -small" 
    src="${window.ACC.config.commonResourcePath}/images/icons/check-circle-green.png" alt=""/>`;

  private readonly ICON_VAN = `<img class="icon -small" 
    src="${window.ACC.config.commonResourcePath}/images/icons/van.png" alt=""/>`;

  private readonly ICON_ALERT = `<img class="icon -small" 
    src="${window.ACC.config.commonResourcePath}/images/icons/alert-round-orange-bold.png" alt=""/>`;

  constructor(private container: TippyHTMLElement, private deliveryData?: StockForecast[]) {
    this.render();
  }

  destroyTooltipInstance():void {
    /* eslint-disable */
    if (this.container._tippy) {
      this.container._tippy.destroy();
    }
    /* eslint-enable */
  }

  initTooltip(content = ''): Tooltip {
    this.destroyTooltipInstance();

    return new Tooltip(this.container, content, {
      interactive: true,
      trigger: 'mouseenter click',
      delay: [2000, 0],
      placement: 'top-end',
      onTrigger(instance, event) {
        // show the tooltip on click immediately, do not wait for delay to pass
        if (event.type === 'click') {
          instance.show();
        }
      },
    });
  }

  render(): void {
    const asyncDataUsed = !this.container.classList.contains('is-model-based');
    const { renderType } = this.container.dataset;
    const renderAs: 'full' | 'reduced' = renderType === 'full' ? 'full' : 'reduced';

    let tooltipContent = '';
    let multipleForecasts = false;
    let unknownDelivery = false;

    if (asyncDataUsed && this.deliveryData && this.deliveryData.length > 0) {
      multipleForecasts = this.deliveryData.length > 1;
      const firstForecastData = this.deliveryData[0];
      const listElements = this.renderDeliveryList(this.deliveryData);
      const articleTemplate = this.renderAvailabilityTemplate(listElements);

      if (renderAs === 'reduced') {
        this.destroyTooltipInstance();
        this.container.classList.remove('has-more-info');
        unknownDelivery = DeliveryAvailability.verifyDate(firstForecastData.date);
        /* eslint-disable */
        if (unknownDelivery) {
          this.container.innerHTML = `
            ${this.ICON_VAN} ${MessageHelper.getMessageWithAttributes(
              'eparts.product.deliveryAvailability.unknownDelivery',
              firstForecastData.quantity.toString(),
            )} ${this.ICON_ARROW}
          `;
        } else {
          const arrivingByMsgCode = firstForecastData.quantity > 1 ? 'eparts.product.deliveryAvailability.arrivingBy.plural' : 'eparts.product.deliveryAvailability.arrivingBy';
          this.container.innerHTML = `
            ${this.ICON_VAN} ${MessageHelper.getMessageWithAttributes(
              arrivingByMsgCode,
              firstForecastData.quantity.toString(),
              firstForecastData.dateFormatted,
            )} ${(multipleForecasts ? this.ICON_ARROW : '')}
          `;
        }
        /* eslint-enable */
        tooltipContent = articleTemplate;
      }

      if (renderAs === 'full') {
        this.container.innerHTML = articleTemplate;
      }
    } else {
      // Here we prepare tooltip content for non-async version, where data is used from the model
      this.initTooltip((this.container.querySelector('[data-forecast-template]') as HTMLElement).innerHTML);
    }

    if (renderAs === 'reduced' && (multipleForecasts || unknownDelivery)) {
      this.initTooltip(tooltipContent);
      // add class for styling purposes only
      this.container.classList.add('has-more-info');
    }
  }

  static arrivalString(forecast: StockForecast): string {
    const arrivingByMsgCode = forecast.quantity > 1 ? 'eparts.product.deliveryAvailability.arrivingBy.plural' : 'eparts.product.deliveryAvailability.arrivingBy';
    return (`
      ${MessageHelper.getMessageWithAttributes(arrivingByMsgCode,
        forecast.quantity.toString(),
        forecast.dateFormatted)}
    `);
  }

  private static verifyDate(date: number): boolean {
    const invalidDate = new Date('9999-12-31').toDateString();
    const dateString = new Date(date).toDateString();
    return dateString === invalidDate;
  }

  renderDeliveryList(forecasts: StockForecast[]): string {
    const listItems = forecasts.map((forecast) => {
      const { alternativeUrl } = this.container.dataset;
      const unknownDelivery = DeliveryAvailability.verifyDate(forecast.date);

      if (unknownDelivery) {
        const alternativeMessage = alternativeUrl ? MessageHelper.getMessageWithAttributes(
          'eparts.product.deliveryAvailability.alternatives',
          `${window.ACC.config.contextPath}${alternativeUrl}`,
        ) : '';
        const contactString = `${MessageHelper.getMessage('eparts.product.deliveryAvailability.contactInfo') || ''} ${alternativeMessage}`;
        const notDeliverableMessage = MessageHelper.getMessageWithAttributes(
          'eparts.product.deliveryAvailability.itemsNotDeliverable',
          forecast.quantity.toString(),
        );
        /* eslint-disable */
        return (`
          <li class="product-availability__list-item" data-delivery-list-item-undeliverable="">
            <div class="product-availability__non-deliverable">
              ${this.ICON_ALERT}
              <div>
                <h3 class="product-availability__non-deliverable-title">${notDeliverableMessage}</h3>
                <p>${contactString}</p>
              </div>
            </div>
          </li>  
        `);
        /* eslint-enable */
      }

      return (`
        <li class="product-availability__list-item">
        ${this.ICON_CHECK} ${MessageHelper.getMessageWithAttributes(
          forecast.quantity > 1 ? 'eparts.product.deliveryAvailability.arrivingBy.plural' : 'eparts.product.deliveryAvailability.arrivingBy',
          forecast.quantity.toString(),
          forecast.dateFormatted,
        )}
        </li>  
      `);
    });

    return (`
      <ul class="product-availability__list">
        ${listItems.join('')}
      </ul>
    `);
  }

  renderAvailabilityTemplate(listElementsMarkup: string): string {
    return (`
      <article class="product-availability">
        <h2 class="product-availability__name">${this.ICON_VAN}${MessageHelper.getMessage('eparts.product.deliveryAvailability.sectionTitle')}</h2>
        ${listElementsMarkup}
      </article>
    `);
  }
}
